import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { decodeJwt, type JwtClaims, type SubscriptionStatus } from '@/shared/lib/jwt';

/* ---- Types ---- */

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  role: string;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  tenantId?: string;
  role?: string;
  permissions: string[];
  subscriptionStatus?: SubscriptionStatus;
  trialExpiresAt?: string; // ISO 8601
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  tenants: TenantSummary[];
  selectedTenant: TenantSummary | null;

  // Actions
  setTokens: (access: string, refresh: string) => void;
  setTenants: (tenants: TenantSummary[]) => void;
  setSelectedTenant: (tenant: TenantSummary) => void;
  clearAuth: () => void;
}

function userFromToken(token: string): AuthUser | null {
  const claims: JwtClaims | null = decodeJwt(token);
  if (!claims) return null;
  return {
    id: claims.sub,
    email: claims.email,
    name: claims.name,
    tenantId: claims.tenant_id,
    role: claims.role,
    permissions: claims.permissions || [],
    subscriptionStatus: claims.subscription_status,
    trialExpiresAt: claims.trial_expires_at,
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      tenants: [],
      selectedTenant: null,

      setTokens: (access, refresh) =>
        set({
          accessToken: access,
          refreshToken: refresh,
          user: userFromToken(access),
        }),

      setTenants: (tenants) => set({ tenants }),

      setSelectedTenant: (tenant) => set({ selectedTenant: tenant }),

      clearAuth: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          tenants: [],
          selectedTenant: null,
        }),
    }),
    {
      name: 'vapt-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        tenants: state.tenants,
        selectedTenant: state.selectedTenant,
      }),
    },
  ),
);
